import * as Sentry from "@sentry/browser";

import { featureOn } from "@lib/featureFlipper";

import { realtimeFallbackPageLoadGraceMs } from "./constants";
import { isDustUnreachableInTab } from "./dustUnreachableLatch";
import { getDustSingletonIfExists } from "./getDustSingleton";
import type { DustConfigUnavailable, RealtimeFallbackReason, RealtimeTransport } from "./types";

const reportedReasons = new Set<RealtimeFallbackReason>();

let dustConfigUnavailable: DustConfigUnavailable | null = null;

/**
 * Reports a fallback to Pusher once per tab per reason. A tab that cannot use DUST would
 * otherwise report on every async request it makes.
 *
 * The unreachable latch is not reported here: DustProvider already reports it when it trips.
 *
 * @param reason Why this tab is answered on Pusher.
 */
const reportFallback = (reason: RealtimeFallbackReason): void => {
  if (reportedReasons.has(reason)) {
    return;
  }

  if (reason === "not-yet-connected" && performance.now() < realtimeFallbackPageLoadGraceMs) {
    return;
  }

  reportedReasons.add(reason);
  // Why: a tab the flippers put on DUST but answered on Pusher is the failure the Pusher fallback hides.
  // When it fires: group by `reason`; no-connection points at /dust/auth (see `extra.dustConfig`), the
  // others at the DUST host or the tab's network (see `extra.dust`). Remove once DUST is the sole
  // transport and Pusher is retired.
  Sentry.captureMessage("Realtime fell back to Pusher", {
    level: "warning",
    tags: { component: "getRealtimeTransport", transport: "dust-direct", reason },
    extra: { dust: getDustSingletonIfExists()?.getConnectionDiagnostics(), dustConfig: dustConfigUnavailable },
  });
};

/**
 * Records that this tab's /dust/auth request settled without a usable config, so it will not get
 * a DUST connection. Until this is called, a tab without a connection is taken to be still loading
 * its config, which a slow page load can stretch well past any fixed grace period.
 *
 * @param details What the request settled with.
 */
export const markDustConfigUnavailable = (details: DustConfigUnavailable): void => {
  dustConfigUnavailable = details;
};

/**
 * Transport this tab can receive an async result on right now. The backend answers a request
 * on DUST only when the request said "dust", so this says "dust" only while the tab's DUST
 * connection is up — any doubt answers "pusher", which every tab listens on.
 *
 * Mirrors useOperatorStreamingTransport: DUST needs the flippers, and a tab that has latched
 * DUST as unreachable stays on Pusher until the verdict expires.
 *
 * @return "dust" when the flippers want DUST and this tab holds a live DUST connection.
 */
export const getRealtimeTransport = (): RealtimeTransport => {
  if (!featureOn("dust_enable_dashboard_connections") || !featureOn("dust_realtime_subscriptions")) {
    return "pusher";
  }

  if (isDustUnreachableInTab()) {
    return "pusher";
  }

  const dustProvider = getDustSingletonIfExists();
  if (!dustProvider) {
    if (dustConfigUnavailable) {
      reportFallback("no-connection");
    }
    return "pusher";
  }

  if (!dustProvider.isConnected()) {
    reportFallback(dustProvider.getConnectionDiagnostics().wasEverConnected ? "disconnected" : "not-yet-connected");
    return "pusher";
  }

  return "dust";
};

/**
 * Drops the once-per-tab reporting memory and the recorded config outcome. Intended for tests.
 */
export const resetRealtimeTransportReporting = (): void => {
  reportedReasons.clear();
  dustConfigUnavailable = null;
};
